import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { AddRounded, AutoAwesomeOutlined, NoteAddOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { useApp } from '../context/AppContext';
import { openDocumentEditor } from '../utils/navigation';
import { EmptyState, SectionCard } from './pagekit';
import DocumentCard from './documents/DocumentCard';
import DocumentActionsMenu from './documents/DocumentActionsMenu';
import useDocumentExport from './documents/useDocumentExport';
import { buildDocumentSnippet, isPresentationDocument, summarizeFieldStatus } from './documents/documentMeta';

const HIGHLIGHT_SCROLL_DELAY_MS = 500;
const HIGHLIGHT_DURATION_MS = 5000;
const UNKNOWN_TEMPLATE = 'Other documents';

/**
 * Plural helper.
 * @param {number} count - Count.
 * @param {string} word - Singular word.
 * @returns {string} "1 word" / "2 words".
 */
const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * Project Documents tab: documents grouped by template, with generate and blank-document actions.
 * @param {object} props - Props.
 * @param {object} props.project - Project (with generatedDocuments, requirements, sessions).
 * @param {function(object): void} props.onPreviewDocument - Open the preview for a document.
 * @param {function(object): void} props.onEditDocument - Open a document in the editor.
 * @param {function(string): void} props.onDeleteDocument - Delete a document by id (caller confirms).
 * @returns {JSX.Element} Tab content.
 */
const DocumentsTab = memo(({ project, onPreviewDocument, onEditDocument, onDeleteDocument }) => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const [searchParams] = useSearchParams();
  const { exporting, exportDocument } = useDocumentExport();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [highlightedDocId, setHighlightedDocId] = useState(null);
  const [creatingBlankDoc, setCreatingBlankDoc] = useState(false);
  const [blankDocError, setBlankDocError] = useState('');

  const highlightParam = searchParams.get('highlight');

  // Scroll to and briefly highlight ?highlight=<documentId> (set by the generation notification).
  useEffect(() => {
    if (!highlightParam) return undefined;
    setHighlightedDocId(highlightParam);
    const scrollTimer = setTimeout(() => {
      document.getElementById(`doc-${highlightParam}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, HIGHLIGHT_SCROLL_DELAY_MS);
    const clearTimer = setTimeout(() => {
      setHighlightedDocId(null);
      const next = new URLSearchParams(window.location.search);
      next.delete('highlight');
      navigate({ search: next.toString() }, { replace: true });
    }, HIGHLIGHT_DURATION_MS);
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(clearTimer);
    };
  }, [highlightParam, navigate]);

  const documents = project.generatedDocuments;

  // Snippets and field summaries are derived once per documents list, not on every render.
  const cardData = useMemo(() => {
    const map = new Map();
    (documents || []).forEach((doc) => {
      map.set(doc.id, {
        snippet: buildDocumentSnippet(doc.content),
        fieldSummary: summarizeFieldStatus(doc),
        isPresentation: isPresentationDocument(doc, state.templates),
      });
    });
    return map;
  }, [documents, state.templates]);

  const groups = useMemo(() => {
    const byTemplate = new Map();
    (documents || []).forEach((doc) => {
      const key = doc.templateName || UNKNOWN_TEMPLATE;
      if (!byTemplate.has(key)) byTemplate.set(key, []);
      byTemplate.get(key).push(doc);
    });
    return Array.from(byTemplate.entries());
  }, [documents]);

  const generatePath = `/generate?customerId=${encodeURIComponent(project.customer_id || '')}&projectId=${encodeURIComponent(project.id)}`;
  const requirementCount = project.requirements?.length || 0;
  const sessionCount = project.sessions?.length || 0;

  const handleMenuOpen = useCallback((event, doc) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedDocument(doc);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedDocument(null);
  }, []);

  /**
   * Run a menu action against the selected document, then close the menu.
   * @param {function(object): void} fn - Action.
   * @returns {void}
   */
  const runMenuAction = (fn) => {
    const doc = selectedDocument;
    handleMenuClose();
    if (doc) fn(doc);
  };

  /**
   * Creates a blank document in this project and opens it in the in-app editor.
   * @returns {Promise<void>}
   */
  const handleCreateBlankDocument = async () => {
    setCreatingBlankDoc(true);
    setBlankDocError('');
    try {
      const result = await actions.addGeneratedDocument({
        projectId: project.id,
        name: 'New Document',
        content: '<p></p>',
        templateId: null,
        templateName: null,
        requirementsCount: 0,
      });
      if (!result.success) throw new Error(result.error || 'Failed to create document');
      openDocumentEditor(navigate, { documentId: result.data.id, projectId: project.id, title: 'New Document' });
    } catch (error) {
      console.error('Error creating blank document:', error);
      setBlankDocError(error.message || 'Could not create a blank document.');
    } finally {
      setCreatingBlankDoc(false);
    }
  };

  const headerActions = (
    <>
      <Button
        variant="outlined"
        startIcon={creatingBlankDoc ? <CircularProgress size={16} color="inherit" /> : <NoteAddOutlined />}
        onClick={handleCreateBlankDocument}
        disabled={creatingBlankDoc}
      >
        {creatingBlankDoc ? 'Creating…' : 'Blank document'}
      </Button>
      <Button variant="contained" startIcon={<AddRounded />} onClick={() => navigate(generatePath)}>
        Generate document
      </Button>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography variant="h6" component="h2">
            Documents
            {documents?.length ? (
              <Typography component="span" sx={{ ml: 1, fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
                {documents.length}
              </Typography>
            ) : null}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generated from this project's {plural(requirementCount, 'requirement')} and {plural(sessionCount, 'session')}.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{headerActions}</Box>
      </Box>

      {blankDocError && (
        <Typography variant="body2" color="error" role="alert">{blankDocError}</Typography>
      )}

      {!documents?.length ? (
        <SectionCard>
          <EmptyState
            inky
            title="No documents yet"
            description={requirementCount > 0
              ? `Pick a template and Inky fills it from this project's ${plural(requirementCount, 'requirement')}. Fixed template text stays exactly as written.`
              : 'Extract requirements from a session first, then generate a document from a template. You can also start from a blank page.'}
            actions={(
              <>
                <Button variant="contained" startIcon={<AutoAwesomeOutlined />} onClick={() => navigate(generatePath)}>
                  Generate document
                </Button>
                <Button variant="outlined" onClick={handleCreateBlankDocument} disabled={creatingBlankDoc}>
                  Start blank
                </Button>
              </>
            )}
          />
        </SectionCard>
      ) : (
        groups.map(([templateName, docs]) => (
          <Box component="section" key={templateName} aria-label={templateName} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
              <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}>
                {templateName}
              </Typography>
              <Typography variant="body2" color="text.secondary">{docs.length}</Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                gap: 2,
              }}
            >
              {docs.map((doc) => {
                const meta = cardData.get(doc.id) || {};
                const busy = exporting?.id === doc.id ? `Exporting ${exporting.type.toUpperCase()}` : null;
                return (
                  <DocumentCard
                    key={doc.id}
                    id={`doc-${doc.id}`}
                    document={doc}
                    snippet={meta.snippet}
                    fieldSummary={meta.fieldSummary}
                    isPresentation={meta.isPresentation}
                    metaLine={`${formatDate(doc.createdAt)} · ${plural(doc.requirementsCount || 0, 'requirement')}`}
                    highlighted={highlightedDocId === doc.id}
                    busyLabel={busy}
                    onOpen={() => onPreviewDocument(doc)}
                    onMenuOpen={(event) => handleMenuOpen(event, doc)}
                    chips={highlightedDocId === doc.id ? <Chip size="small" color="primary" label="New" /> : null}
                  />
                );
              })}
            </Box>
          </Box>
        ))
      )}

      <DocumentActionsMenu
        anchorEl={menuAnchorEl}
        isPresentation={Boolean(selectedDocument && cardData.get(selectedDocument.id)?.isPresentation)}
        exportDisabled={Boolean(exporting)}
        onClose={handleMenuClose}
        onPreview={() => runMenuAction(onPreviewDocument)}
        onEdit={() => runMenuAction(onEditDocument)}
        onOpenNewTab={() => runMenuAction((doc) => openDocumentEditor(navigate, { documentId: doc.id, projectId: project.id, newTab: true }))}
        onExport={(type) => runMenuAction((doc) => exportDocument(doc, type, project.name))}
        onDelete={() => runMenuAction((doc) => onDeleteDocument(doc.id))}
      />
    </Box>
  );
});

DocumentsTab.displayName = 'DocumentsTab';

export default DocumentsTab;
